import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Grid,
    Card,
    Typography,
    Box,
    Chip,
    Paper,
    TextField,
    InputAdornment,
    Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMainPageStatuses } from '../../hooks/useMainPageStatuse';
import { option as staticOptions } from './Option';
import featureVisibilityService from '../../services/featureVisibilityService';
import { getIcon } from '../../config/iconMap';
import SecurityIcon from '@mui/icons-material/Security';
import SchoolIcon from '@mui/icons-material/School';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { initializeDB } from '../../config/db';
import { INCIDENT_STORE } from '../../config/constants';
import { useWeeklyRoster } from '../../hooks/useWeeklyRoster';
import IconButton from '@mui/material/IconButton';

export default function MainPageNew() {
    const navigate = useNavigate();
    const { ccStatus, trainingStatuses, ssdStatuses, jhaStatuses, dailyLogStatuses } = useMainPageStatuses();
    const { hasCurrentWeekRoster, currentWeekEmployeeCount, permanentCount, tempCount } = useWeeklyRoster();

    // Search and filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Incident statuses
    const [incidentStatuses, setIncidentStatuses] = useState({ processing: 0, submitted: 0 });

    // Feature visibility — reactive: re-read localStorage on focus/navigation/sync
    const [dbFeatures, setDbFeatures] = useState(() => featureVisibilityService.getFeatures());

    useEffect(() => {
        const reload = () => setDbFeatures(featureVisibilityService.getFeatures());

        // Re-read when user returns to the tab (after admin changes)
        window.addEventListener('focus', reload);
        // Re-read when localStorage changes (sync completed in another context)
        window.addEventListener('storage', (e) => {
            if (e.key === 'featureVisibility') reload();
        });
        // Poll every 10s as fallback (sync may update localStorage in same tab)
        const interval = setInterval(reload, 10000);

        return () => {
            window.removeEventListener('focus', reload);
            window.removeEventListener('storage', reload);
            clearInterval(interval);
        };
    }, []);

    // Load incident statuses
    useEffect(() => {
        loadIncidentStatuses();
        // Reload every 30 seconds
        const interval = setInterval(loadIncidentStatuses, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadIncidentStatuses = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(INCIDENT_STORE, 'readonly');
            const store = tx.objectStore(INCIDENT_STORE);
            const statusIndex = store.index('status');

            const processing = await statusIndex.getAll('processing');
            const submitted = await statusIndex.getAll('submitted');

            setIncidentStatuses({
                processing: processing.length,
                submitted: submitted.length
            });
        } catch (error) {
            console.error('Error loading incident statuses:', error);
        }
    };

    const rawRoles = localStorage.getItem('roles');
    let userRoles = [];

    if (rawRoles) {
        try {
            const parsed = JSON.parse(rawRoles);
            userRoles = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            userRoles = [rawRoles];
        }
    }

    // Keys that should NOT be rendered as tiles on main page
    const EXCLUDED_TILE_KEYS = [
        'frontend_main',              // this IS the main page
        'frontend_synchronization',   // sync page, not a tile
        'frontend_continuous_improvement', // rendered as separate block below
        'frontend_aerial_scissor',    // not a main page tile
        'statement',                  // not a main page tile
    ];

    // Category display order
    const CATEGORY_ORDER = ['safety', 'training', 'operations', 'piping', 'electrical'];

    // Build options from sync data (DB-driven) with fallback to static Option.jsx
    const allOptions = useMemo(() => {
        // If no sync data yet — use static options as-is (fail-open)
        if (!dbFeatures || dbFeatures.length === 0) {
            return staticOptions;
        }

        // Build options from DB features, using static options for icon fallback
        const staticByKey = {};
        for (const opt of staticOptions) {
            if (opt.featureKey) staticByKey[opt.featureKey] = opt;
        }

        const options = dbFeatures
            .filter(f => !EXCLUDED_TILE_KEYS.includes(f.featureKey))
            .map(f => {
                const staticOpt = staticByKey[f.featureKey];
                // Use DB icon name → React component, fallback to static icon
                const icon = f.icon
                    ? getIcon(f.icon, { fontSize: 'large' })
                    : (staticOpt?.icon || getIcon('HelpOutline', { fontSize: 'large' }));

                return {
                    name: f.name || f.displayName || (staticOpt?.name) || f.featureKey,
                    displayName: f.displayName || f.name || (staticOpt?.displayName) || f.featureKey,
                    featureKey: f.featureKey,
                    icon: icon,
                    link: f.link || (staticOpt?.link) || '/',
                    color: f.color || (staticOpt?.color) || '#607d8b',
                    description: f.description || (staticOpt?.description) || '',
                    category: f.category || (staticOpt?.category) || 'operations',
                    searchTerms: f.searchTerms || (staticOpt?.searchTerms) || [],
                    roles: f.roles || (staticOpt?.roles) || [],
                    sortOrder: f.sortOrder ?? (staticOpt?.sortOrder) ?? 0,
                };
            });

        // Sort: by category order, then by sortOrder within category
        options.sort((a, b) => {
            const catA = CATEGORY_ORDER.indexOf(a.category);
            const catB = CATEGORY_ORDER.indexOf(b.category);
            const orderA = catA >= 0 ? catA : 999;
            const orderB = catB >= 0 ? catB : 999;
            if (orderA !== orderB) return orderA - orderB;
            return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        return options;
    }, [dbFeatures]);

    const visibleOptions = allOptions.filter(item => {
        // Role-based filter
        if (item.roles && item.roles.length > 0 && !item.roles.some(r => userRoles.includes(r))) return false;
        return true;
    });

    // Filter by search query and category
    const filteredOptions = visibleOptions.filter(opt => {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = searchQuery === '' ||
            opt.displayName.toLowerCase().includes(searchLower) ||
            opt.description?.toLowerCase().includes(searchLower) ||
            opt.name.toLowerCase().includes(searchLower) ||
            opt.searchTerms?.some(term => term.toLowerCase().includes(searchLower));

        const matchesCategory = selectedCategory === 'all' || opt.category === selectedCategory;

        return matchesSearch && matchesCategory;
    });

    // Category filter handler
    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    // Categories for filter chips
    const categories = [
        { id: 'all', label: 'All', icon: null, color: '#757575', count: visibleOptions.length },
        { id: 'safety', label: 'Safety', icon: <SecurityIcon sx={{ fontSize: 18 }} />, color: '#d32f2f', count: visibleOptions.filter(o => o.category === 'safety').length },
        { id: 'training', label: 'Training', icon: <SchoolIcon sx={{ fontSize: 18 }} />, color: '#1976d2', count: visibleOptions.filter(o => o.category === 'training').length },
        { id: 'operations', label: 'Operations', icon: <BusinessCenterIcon sx={{ fontSize: 18 }} />, color: '#2e7d32', count: visibleOptions.filter(o => o.category === 'operations').length },
        { id: 'piping', label: 'Piping', icon: <PlumbingIcon sx={{ fontSize: 18 }} />, color: '#00acc1', count: visibleOptions.filter(o => o.category === 'piping').length },
        { id: 'electrical', label: 'Electrical', icon: <ElectricBoltIcon sx={{ fontSize: 18 }} />, color: '#ff9800', count: visibleOptions.filter(o => o.category === 'electrical').length },
        { id: 'tools_equipment', label: 'Tools & Equipment', icon: <BuildIcon sx={{ fontSize: 18 }} />, color: '#e65100', count: visibleOptions.filter(o => o.category === 'tools_equipment').length },
    ];

    // Get category info by id
    const getCategoryInfo = (categoryId) => {
        return categories.find(cat => cat.id === categoryId) || {};
    };

    const getStatusInfo = (optionName) => {
        if (optionName === 'CC') {
            const hasInProcess = ccStatus === 'In Process' || ccStatus === 'in process' || ccStatus === 'process';
            return {
                total: hasInProcess ? 1 : 0,
                statuses: hasInProcess ? [{ label: 'In Process', count: '', color: '#ff9800' }] : []
            };
        }

        if (optionName === 'Training') {
            const total = Object.values(trainingStatuses).reduce((a, b) => a + b, 0);
            const statuses = Object.entries(trainingStatuses).map(([status, count]) => ({
                label: status.charAt(0).toUpperCase() + status.slice(1),
                count,
                color: status === 'complete' ? '#4caf50' : '#ff9800'
            }));

            return {
                total,
                statuses
            };
        }

        if (optionName === 'Safety Stand Down') {
            const hasInProcess = ssdStatuses.process > 0 || ssdStatuses.pending > 0 || ssdStatuses.active > 0;
            return {
                total: hasInProcess ? 1 : 0,
                statuses: hasInProcess ? [{ label: 'In Process', count: '', color: '#ff9800' }] : []
            };
        }

        if (optionName === 'PO') {
            return {
                total: 0,
                statuses: []
            };
        }

        if (optionName === 'JHA') {
            const total = Object.values(jhaStatuses).reduce((a, b) => a + b, 0);
            const draftCount = jhaStatuses.draft || 0;
            const savedCount = jhaStatuses.saved || 0;
            const submittedCount = jhaStatuses.submitted || 0;
            const completedCount = jhaStatuses.completed || 0;
            const processCount = jhaStatuses.process || jhaStatuses.inProcess || jhaStatuses['in process'] || 0;

            const statuses = [];
            if (draftCount > 0) statuses.push({ label: 'Draft', count: draftCount, color: '#9e9e9e' });
            if (savedCount > 0) statuses.push({ label: 'Saved', count: savedCount, color: '#2196f3' });
            if (processCount > 0) statuses.push({ label: 'In Process', count: processCount, color: '#ff9800' });
            if (submittedCount > 0) statuses.push({ label: 'Submitted', count: submittedCount, color: '#ff9800' });
            if (completedCount > 0) statuses.push({ label: 'Completed', count: completedCount, color: '#4caf50' });

            const actualTotal = total + processCount;

            return {
                total: actualTotal,
                statuses
            };
        }

        if (optionName === 'Daily Log') {
            const pendingCount = dailyLogStatuses.pending || 0;
            const syncedCount = dailyLogStatuses.synced || 0;
            const failedCount = dailyLogStatuses.failed || 0;
            const total = dailyLogStatuses.total || 0;

            const statuses = [];
            if (pendingCount > 0) statuses.push({ label: 'Pending', count: '', color: '#ff9800' });
            else if (syncedCount > 0) statuses.push({ label: 'Sent', count: '', color: '#4caf50' });
            else if (failedCount > 0) statuses.push({ label: 'Failed', count: '', color: '#f44336' });

            return {
                total,
                statuses,
                emptyMessage: 'No log today'
            };
        }

        if (optionName === 'Incidents') {
            const processingCount = incidentStatuses.processing || 0;
            const submittedCount = incidentStatuses.submitted || 0;
            const total = processingCount + submittedCount;

            const statuses = [];
            if (processingCount > 0) statuses.push({ label: 'Draft', count: processingCount, color: '#ff9800' });
            if (submittedCount > 0) statuses.push({ label: 'Submitted', count: submittedCount, color: '#4caf50' });

            return {
                total,
                statuses
            };
        }

        if (optionName === 'Weekly Roster') {
            const statuses = [];
            if (hasCurrentWeekRoster) {
                if (permanentCount > 0) statuses.push({ label: 'Permanent', count: permanentCount, color: '#4caf50' });
                if (tempCount > 0) statuses.push({ label: 'Temporary', count: tempCount, color: '#2196f3' });
            }

            return {
                total: currentWeekEmployeeCount,
                statuses,
                emptyMessage: 'No roster for this week'
            };
        }

        // Default return
        return {
            total: 0,
            statuses: []
        };
    };

    const renderCard = (opt) => {
        const statusInfo = getStatusInfo(opt.name);
        const categoryInfo = getCategoryInfo(opt.category);

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={opt.name}>
                <Card
                    onClick={() => navigate(opt.link)}
                    sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: `4px solid ${categoryInfo.color || opt.color}`,
                        backgroundColor: 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: 3,
                            borderLeftColor: categoryInfo.color || opt.color,
                            borderLeftWidth: '6px',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        }
                    }}
                >
                    <Box sx={{ py: 1.2, px: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Header with icon and title */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: categoryInfo.color || opt.color,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1,
                                flexShrink: 0
                            }}>
                                {React.cloneElement(opt.icon, {
                                    sx: { fontSize: 18, color: 'white' }
                                })}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        lineHeight: 1.1,
                                        color: '#333',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {opt.displayName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.65rem',
                                        lineHeight: 1.1,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {opt.description}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Card>
            </Grid>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 2 }}>
            {/* Search and Filter Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: '#ffffff',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                }}
            >
                {/* Search Field */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search forms by name or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setSearchQuery('')}
                                    edge="end"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{ mb: 2 }}
                />

                {/* Category Filter Chips */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {categories.map((cat) => (
                        <Chip
                            key={cat.id}
                            label={`${cat.label} (${cat.count})`}
                            icon={cat.icon}
                            onClick={() => handleCategoryClick(cat.id)}
                            variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
                            sx={{
                                backgroundColor: selectedCategory === cat.id ? cat.color : 'transparent',
                                color: selectedCategory === cat.id ? 'white' : cat.color,
                                borderColor: cat.color,
                                '&:hover': {
                                    backgroundColor: selectedCategory === cat.id ? cat.color : `${cat.color}15`,
                                },
                                fontWeight: selectedCategory === cat.id ? 600 : 400,
                                '& .MuiChip-icon': {
                                    color: selectedCategory === cat.id ? 'white' : cat.color,
                                }
                            }}
                        />
                    ))}
                </Stack>

                {/* Results count */}
                {(searchQuery || selectedCategory !== 'all') && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Found {filteredOptions.length} form{filteredOptions.length !== 1 ? 's' : ''}
                            {searchQuery && ` matching "${searchQuery}"`}
                            {selectedCategory !== 'all' && ` in ${categories.find(c => c.id === selectedCategory)?.label}`}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Continuous Improvement link */}
            <Box
                onClick={() => navigate('/continuous-improvement')}
                sx={{
                    mb: 2,
                    py: 0.75,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#ffecb3' }
                }}
            >
                <TipsAndUpdatesIcon sx={{
                    color: '#ff9800',
                    fontSize: 18,
                    animation: 'wiggle 2.5s ease-in-out infinite',
                    '@keyframes wiggle': {
                        '0%, 100%': { transform: 'rotate(0deg)' },
                        '10%': { transform: 'rotate(-12deg)' },
                        '20%': { transform: 'rotate(10deg)' },
                        '30%': { transform: 'rotate(-8deg)' },
                        '40%': { transform: 'rotate(6deg)' },
                        '50%': { transform: 'rotate(0deg)' }
                    }
                }} />
                <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                    Submit Improvement Idea
                </Typography>
            </Box>

            {/* Unified Cards Grid - All filtered items */}
            {filteredOptions.length > 0 ? (
                <Grid container spacing={2}>
                    {filteredOptions.map(renderCard)}
                </Grid>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#ffffff',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No forms found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchQuery
                            ? `No forms match "${searchQuery}"`
                            : `No forms available in ${categories.find(c => c.id === selectedCategory)?.label || 'this category'}`
                        }
                    </Typography>
                </Paper>
            )}
        </Container>
    );
}
